import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Check, X, Tag, Trash2 } from 'lucide-react';
import type { MembershipPlan, PlanType, PlanDurationUnit, PackageKind } from '@techsofit/api-types';
import { useAuth } from '@techsofit/app-kit';
import { Button, Card, Input, Modal, Select, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useGymData } from '../data/GymDataProvider';
import { createPlan, updatePlan, deletePlan } from '../lib/gymApi';
import { ApiClientError } from '@techsofit/app-kit';
import { money, planDuration } from '../lib/format';

interface Preset {
  label: string;
  name: string;
  durationUnit: PlanDurationUnit;
  durationCount: number;
  type: PlanType;
  packageKind?: PackageKind;
  coachRequired?: boolean;
  hint?: string;
}

const ACCESS_PRESETS: Preset[] = [
  { label: 'Day Pass', name: 'Day Pass', durationUnit: 'DAY', durationCount: 1, type: 'PASS', hint: 'Walk in for one day' },
  { label: 'Weekly', name: 'Weekly', durationUnit: 'DAY', durationCount: 7, type: 'RECURRING', hint: '7 days of gym access' },
  { label: 'Monthly', name: 'Monthly', durationUnit: 'MONTH', durationCount: 1, type: 'RECURRING', hint: 'Most common' },
  { label: 'Quarterly', name: 'Quarterly', durationUnit: 'MONTH', durationCount: 3, type: 'RECURRING' },
  { label: 'Half-year', name: 'Half-Year', durationUnit: 'MONTH', durationCount: 6, type: 'RECURRING' },
  { label: 'Annual', name: 'Annual', durationUnit: 'MONTH', durationCount: 12, type: 'RECURRING' },
];

const PACKAGE_PRESETS: Preset[] = [
  { label: '1 PT session', name: 'Personal Training', durationUnit: 'DAY', durationCount: 1, type: 'PERSONAL_TRAINING', packageKind: 'PERSONAL_TRAINING', coachRequired: true, hint: 'One session with a coach' },
  { label: 'Monthly PT', name: 'Monthly PT', durationUnit: 'MONTH', durationCount: 1, type: 'PERSONAL_TRAINING', packageKind: 'PERSONAL_TRAINING', coachRequired: true, hint: 'Valid for a month' },
  { label: 'Drop-in class', name: 'Drop-in class', durationUnit: 'DAY', durationCount: 1, type: 'PERSONAL_TRAINING', packageKind: 'CLASS', coachRequired: false, hint: 'Yoga, HIIT, spin…' },
  { label: 'Monthly classes', name: 'Monthly classes', durationUnit: 'MONTH', durationCount: 1, type: 'PERSONAL_TRAINING', packageKind: 'CLASS', coachRequired: false, hint: 'Unlimited classes this month' },
  { label: 'Other extra', name: 'Add-on', durationUnit: 'DAY', durationCount: 1, type: 'PERSONAL_TRAINING', packageKind: 'OTHER', coachRequired: false, hint: 'Nutrition, recovery…' },
];

const TYPES: { value: PlanType; label: string }[] = [
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'PASS', label: 'Pass' },
  { value: 'PERSONAL_TRAINING', label: 'Personal training' },
];

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

type CatalogTab = 'access' | 'packages';

export function PlansPage() {
  const { plans, reloadPlans } = useGymData();
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: CatalogTab = searchParams.get('tab') === 'packages' ? 'packages' : 'access';
  const [adding, setAdding] = useState<{ type: PlanType; preset?: Preset } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const sorted = [...plans].sort((a, b) => Number(a.active === false) - Number(b.active === false));
  const membershipPlans = sorted.filter((p) => p.type !== 'PERSONAL_TRAINING');
  const ptPlans = sorted.filter((p) => p.type === 'PERSONAL_TRAINING');
  const isPackages = tab === 'packages';
  const list = isPackages ? ptPlans : membershipPlans;

  function setTab(next: CatalogTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'access') params.delete('tab');
    else params.set('tab', 'packages');
    setSearchParams(params, { replace: true });
  }

  const renderGrid = (rows: MembershipPlan[], emptyText: string) =>
    rows.length ? (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((plan) =>
          editingId === plan.id ? (
            <EditPlanCard
              key={plan.id}
              plan={plan}
              onCancel={() => setEditingId(null)}
              onSaved={() => {
                setEditingId(null);
                reloadPlans();
              }}
            />
          ) : (
            <PlanCard key={plan.id} plan={plan} canEdit={isOwner} onEdit={() => setEditingId(plan.id)} />
          ),
        )}
      </div>
    ) : (
      <p className="rounded-control border border-dashed border-line px-4 py-6 text-center text-sm text-muted">{emptyText}</p>
    );

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={`${plans.filter((p) => p.active).length} active`}
        title="Plans"
        description="Gym access is walking in. Packages are extras on top — personal training, classes, and anything else you price separately."
      />

      {isOwner && (
        <Modal
          open={adding !== null}
          onClose={() => setAdding(null)}
          title={adding?.type === 'PERSONAL_TRAINING' ? 'Add a package' : 'Add gym access'}
          description={
            adding?.type === 'PERSONAL_TRAINING'
              ? 'Sold on top of gym access. Recording it does not change the member’s walk-in expiry.'
              : 'Ongoing gym access. Recording a payment sets the member’s expiry date.'
          }
        >
          <AddPlanForm
            initialType={adding?.type ?? 'RECURRING'}
            preset={adding?.preset}
            onCancel={() => setAdding(null)}
            onCreated={() => {
              setAdding(null);
              reloadPlans();
            }}
          />
        </Modal>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 border-b border-line">
          <TabBtn
            label={`Gym access${membershipPlans.length ? ` (${membershipPlans.length})` : ''}`}
            active={!isPackages}
            onClick={() => setTab('access')}
          />
          <TabBtn
            label={`Packages${ptPlans.length ? ` (${ptPlans.length})` : ''}`}
            active={isPackages}
            onClick={() => setTab('packages')}
          />
        </div>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdding({ type: isPackages ? 'PERSONAL_TRAINING' : 'RECURRING' })}
          >
            <Plus className="size-4" aria-hidden />
            {isPackages ? 'Add package' : 'Add gym access'}
          </Button>
        )}
      </div>

      <p className="mb-3 max-w-prose text-sm text-muted">
        {isPackages
          ? 'Personal training, classes, and other extras. A package payment does not change gym access dates.'
          : 'What they pay to walk in. Recording this sets the member’s expiry.'}
      </p>

      {isOwner && (
        <ExampleChips
          items={isPackages ? PACKAGE_PRESETS : ACCESS_PRESETS}
          onPick={(preset) => setAdding({ type: preset.type, preset })}
        />
      )}

      <div className="mt-5">
        {renderGrid(
          list,
          isPackages ? 'No packages yet. Tap an example above to create one.' : 'No gym access plans yet. Tap an example above to create one.',
        )}
      </div>
    </section>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px shrink-0 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
        active ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {label}
    </button>
  );
}

function ExampleChips({ items, onPick }: { items: Preset[]; onPick: (p: Preset) => void }) {
  return (
    <div>
      <p className="text-sm font-medium text-ink">Start from an example</p>
      <p className="mt-0.5 text-sm text-muted">Tap one — the name and duration fill in. You add the price.</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((p) => (
          <button
            key={p.label}
            type="button"
            title={p.hint}
            onClick={() => onPick(p)}
            className="rounded-pill bg-surface px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-line-strong hover:text-ink"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, canEdit, onEdit }: { plan: MembershipPlan; canEdit: boolean; onEdit: () => void }) {
  return (
    <Card className={cn('flex flex-col p-5', !plan.active && 'opacity-60')}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="truncate font-display text-lg font-semibold text-ink">{plan.name}</p>
          <span className="mt-1 inline-flex items-center gap-1 rounded-pill bg-surface px-2 py-0.5 text-xs font-medium text-ink-soft">
            <Tag className="size-3" aria-hidden />
            {plan.type === 'PERSONAL_TRAINING'
              ? plan.packageKind === 'CLASS'
                ? 'Class'
                : plan.packageKind === 'OTHER'
                  ? 'Other'
                  : 'Personal training'
              : plan.type.toLowerCase().replace(/_/g, ' ')}
          </span>
        </div>
        {canEdit && (
          <button onClick={onEdit} aria-label={`Edit ${plan.name}`} className="text-muted hover:text-ink">
            <Pencil className="size-4" aria-hidden />
          </button>
        )}
      </div>
      <div className="mt-4 flex items-end justify-between">
        <p className="font-display text-display-md tabular text-ink">{money(plan.price)}</p>
        <p className="text-sm text-muted">
          {plan.type === 'PERSONAL_TRAINING' ? `valid ${planDuration(plan.durationUnit, plan.durationCount)}` : planDuration(plan.durationUnit, plan.durationCount)}
        </p>
      </div>
      {!plan.active && <p className="mt-2 text-xs font-medium text-muted">Inactive — hidden from new payments</p>}
    </Card>
  );
}

function AddPlanForm({
  initialType,
  preset,
  onCancel,
  onCreated,
}: {
  initialType: PlanType;
  preset?: Preset;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const isPt = initialType === 'PERSONAL_TRAINING';
  const examples = isPt ? PACKAGE_PRESETS : ACCESS_PRESETS;
  const [name, setName] = useState(preset?.name ?? '');
  const [price, setPrice] = useState('');
  const [durationCount, setDurationCount] = useState(String(preset?.durationCount ?? 1));
  const [durationUnit, setDurationUnit] = useState<PlanDurationUnit>(preset?.durationUnit ?? 'MONTH');
  const [type, setType] = useState<PlanType>(preset?.type ?? initialType);
  const [packageKind, setPackageKind] = useState<PackageKind>(preset?.packageKind ?? 'PERSONAL_TRAINING');
  const [coachRequired, setCoachRequired] = useState(preset?.coachRequired ?? true);
  const fields = { name, setName, price, setPrice, durationCount, setDurationCount, durationUnit, setDurationUnit, type, setType, packageKind, setPackageKind, coachRequired, setCoachRequired };

  function applyPreset(p: Preset) {
    setName(p.name);
    setDurationCount(String(p.durationCount));
    setDurationUnit(p.durationUnit);
    setType(p.type);
    if (p.packageKind) setPackageKind(p.packageKind);
    if (p.coachRequired != null) setCoachRequired(p.coachRequired);
  }

  return (
    <>
      <p className="text-sm font-medium text-ink">Start from an example</p>
      <p className="mt-0.5 text-sm text-muted">Name and duration fill in. You add the price.</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {examples.map((p) => (
          <button
            key={p.label}
            type="button"
            title={p.hint}
            onClick={() => applyPreset(p)}
            className={cn(
              'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
              name === p.name ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-line-strong hover:text-ink',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <PlanFields {...fields} hideType submitLabel={isPt ? 'Add package' : 'Add gym access'} onCancel={onCancel} onDone={onCreated} mode="create" />
    </>
  );
}

function EditPlanCard({ plan, onCancel, onSaved }: { plan: MembershipPlan; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState(plan.name);
  const [price, setPrice] = useState(plan.price);
  const [durationCount, setDurationCount] = useState(String(plan.durationCount));
  const [durationUnit, setDurationUnit] = useState<PlanDurationUnit>(plan.durationUnit);
  const [type, setType] = useState<PlanType>(plan.type);
  const [packageKind, setPackageKind] = useState<PackageKind>(plan.packageKind ?? 'PERSONAL_TRAINING');
  const [coachRequired, setCoachRequired] = useState(plan.coachRequired !== false);
  const [active, setActive] = useState(plan.active);

  return (
    <Card className="p-5">
      <PlanFields
        name={name}
        setName={setName}
        price={price}
        setPrice={setPrice}
        durationCount={durationCount}
        setDurationCount={setDurationCount}
        durationUnit={durationUnit}
        setDurationUnit={setDurationUnit}
        type={type}
        setType={setType}
        packageKind={packageKind}
        setPackageKind={setPackageKind}
        coachRequired={coachRequired}
        setCoachRequired={setCoachRequired}
        active={active}
        setActive={setActive}
        submitLabel="Save"
        onCancel={onCancel}
        onDone={onSaved}
        mode="edit"
        planId={plan.id}
      />
    </Card>
  );
}

interface PlanFieldsProps {
  name: string;
  setName: (v: string) => void;
  price: string;
  setPrice: (v: string) => void;
  durationCount: string;
  setDurationCount: (v: string) => void;
  durationUnit: PlanDurationUnit;
  setDurationUnit: (v: PlanDurationUnit) => void;
  type: PlanType;
  setType: (v: PlanType) => void;
  packageKind?: PackageKind;
  setPackageKind?: (v: PackageKind) => void;
  coachRequired?: boolean;
  setCoachRequired?: (v: boolean) => void;
  active?: boolean;
  setActive?: (v: boolean) => void;
  /** Hide the type picker — the section already fixes whether this is membership or PT. */
  hideType?: boolean;
  submitLabel: string;
  onCancel: () => void;
  onDone: () => void;
  mode: 'create' | 'edit';
  planId?: string;
}

function PlanFields(p: PlanFieldsProps) {
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = saving || deleting;

  async function remove() {
    if (!p.planId) return;
    const ok = await confirm({
      title: `Delete the “${p.name}” plan?`,
      description: 'This can’t be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setError(null);
    setDeleting(true);
    try {
      await deletePlan(p.planId);
      p.onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the plan.');
      setDeleting(false);
    }
  }

  async function submit() {
    setError(null);
    const count = parseInt(p.durationCount, 10);
    if (!p.name.trim()) return setError('Give the plan a name.');
    if (!(Number(p.price) >= 0) || p.price.trim() === '') return setError('Enter a price.');
    if (!(count >= 1)) return setError('Duration must be at least 1.');
    setSaving(true);
    try {
      if (p.mode === 'create') {
        await createPlan({
          name: p.name.trim(),
          price: Number(p.price).toFixed(2),
          durationUnit: p.durationUnit,
          durationCount: count,
          type: p.type,
          ...(p.type === 'PERSONAL_TRAINING'
            ? { packageKind: p.packageKind ?? 'PERSONAL_TRAINING', coachRequired: p.coachRequired !== false }
            : {}),
        });
      } else {
        await updatePlan(p.planId!, {
          name: p.name.trim(),
          price: Number(p.price).toFixed(2),
          durationUnit: p.durationUnit,
          durationCount: count,
          type: p.type,
          ...(p.type === 'PERSONAL_TRAINING'
            ? { packageKind: p.packageKind ?? 'PERSONAL_TRAINING', coachRequired: p.coachRequired !== false }
            : {}),
          active: p.active,
        });
      }
      p.onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the plan.');
      setSaving(false);
    }
  }

  return (
    <div className={p.mode === 'create' ? 'mt-4 flex flex-col gap-4 border-t border-line pt-4' : 'flex flex-col gap-4'}>
      <Input label="Plan name" value={p.name} onChange={(e) => p.setName(e.target.value)} placeholder={p.type === 'PERSONAL_TRAINING' ? 'e.g. Personal Training' : 'e.g. Monthly'} />
      <Input label="Price" inputMode="decimal" leading="$" value={p.price} onChange={(e) => p.setPrice(e.target.value)} placeholder="0.00" />
      {/* Membership = billing period; PT package = how long the personal training stays valid. */}
      <div className="grid grid-cols-[6rem_1fr] gap-3">
        <Input
          label={p.type === 'PERSONAL_TRAINING' ? 'Valid for' : 'Every'}
          inputMode="numeric"
          value={p.durationCount}
          onChange={(e) => p.setDurationCount(e.target.value)}
        />
        <Select label="Period" value={p.durationUnit} onChange={(e) => p.setDurationUnit(e.target.value as PlanDurationUnit)}>
          <option value="MONTH">Month(s)</option>
          <option value="DAY">Day(s)</option>
        </Select>
      </div>
      {!p.hideType && (
        <Select label="Type" value={p.type} onChange={(e) => p.setType(e.target.value as PlanType)}>
          {/* Keep an existing (now-hidden) type like PACK selectable when editing it. */}
          {(TYPES.some((t) => t.value === p.type) ? TYPES : [{ value: p.type, label: titleCase(p.type) }, ...TYPES]).map(
            (t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ),
          )}
        </Select>
      )}

      {p.type === 'PERSONAL_TRAINING' && p.setPackageKind && (
        <Select
          label="Package kind"
          value={p.packageKind ?? 'PERSONAL_TRAINING'}
          onChange={(e) => {
            const kind = e.target.value as PackageKind;
            p.setPackageKind!(kind);
            if (p.setCoachRequired && p.coachRequired == null) {
              p.setCoachRequired(kind === 'PERSONAL_TRAINING');
            }
          }}
        >
          <option value="PERSONAL_TRAINING">Personal training</option>
          <option value="CLASS">Class</option>
          <option value="OTHER">Other</option>
        </Select>
      )}

      {p.type === 'PERSONAL_TRAINING' && p.setCoachRequired && (
        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
          <input
            type="checkbox"
            checked={p.coachRequired !== false}
            onChange={(e) => p.setCoachRequired!(e.target.checked)}
            className="size-4 rounded border-line accent-ink"
          />
          Coach required when recording a payment
        </label>
      )}

      {p.mode === 'edit' && p.setActive && (
        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
          <input type="checkbox" checked={p.active} onChange={(e) => p.setActive!(e.target.checked)} className="size-4 rounded border-line accent-ink" />
          Active (available when recording payments)
        </label>
      )}

      {error && (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        {p.mode === 'edit' && (
          <Button size="sm" variant="ghost" onClick={remove} loading={deleting} disabled={busy} className="mr-auto text-bad-ink hover:bg-bad-soft">
            {!deleting && <Trash2 className="size-4" aria-hidden />} Delete
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={p.onCancel} disabled={busy}>
          <X className="size-4" aria-hidden /> Cancel
        </Button>
        <Button size="sm" onClick={submit} loading={saving} disabled={busy}>
          {!saving && <Check className="size-4" aria-hidden />} {p.submitLabel}
        </Button>
      </div>
    </div>
  );
}
